/**
 * The functionality in TitleBehaviors used to be contained directly in Frame.
 * Over time, we've split the functionality of frame into different behaviors,
 * primarily FrameBehaviors. After this change, it made less sense for Table to
 * inherit from Frame, and in fact was causing more problems to do so.
 *
 * This behavior is just another refactor of shared functionality between Frame
 * and Table now that Table no longer inherits from Frame.
 */

import { type fabric } from 'fabric';
import { type Constructor } from '../types';
import {
  IMAGE_SCALE,
  type EmbeddedTextBehaviors,
} from './EmbeddedTextBehaviorsV2';

// Visible width below which the frame name is hidden.
const MINIMUM_NAME_WIDTH = 75;

interface TitleBehaviors {
  renderTitle(
    ctx: CanvasRenderingContext2D,
    additionalTitleItems?: Array<(ctx: CanvasRenderingContext2D) => void>
  ): void;
  getLabelZoomScale(zoom: number): number;
  getTextWidth(): number;
  showTitle(): boolean;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
function TitleBehaviors<
  T extends Constructor<
    fabric.Object & EmbeddedTextBehaviors & { showTitle(): boolean }
  >
>(Base: T): T & Constructor<TitleBehaviors> {
  return class extends Base implements TitleBehaviors {
    __hasTitleBehaviors = true;

    // Title behavior implementation will go here

    renderTitle(
      ctx: CanvasRenderingContext2D,
      additionalTitleItems: Array<(ctx: CanvasRenderingContext2D) => void> = []
    ) {
      if (!this.textEditing && this.showTitle()) {
        const zoom = this.canvas.getZoom();

        // When the frame is visibly small compared to the text, fade it out
        // and then hide completely
        const visibleWidth = this.width * this.scaleX * zoom;

        if (visibleWidth >= MINIMUM_NAME_WIDTH) {
          ctx.save();
          // NOTE: Title should maintain full opacity regardless of frame opacity
          ctx.globalAlpha = 1;

          if (visibleWidth < 2 * MINIMUM_NAME_WIDTH) {
            ctx.globalAlpha =
              (visibleWidth - MINIMUM_NAME_WIDTH) / MINIMUM_NAME_WIDTH;
          }

          this.renderTitleText(ctx);
          additionalTitleItems.forEach(item => item(ctx));

          ctx.restore();
        }
      }
    }

    private renderTitleText(ctx: CanvasRenderingContext2D) {
      const image = this.textImage;

      if (image?.complete && image.naturalHeight > 0) {
        const zoom = this.canvas.getZoom();
        const position = this.textPosition();
        const imageScale = IMAGE_SCALE;
        const zoomScale = this.getLabelZoomScale(zoom);
        const textWidth = this.getTextWidth();

        ctx.drawImage(
          image,
          -this.width / 2,
          -this.height / 2 +
            ((position.top - this.top) * zoomScale) / this.scaleY,
          textWidth,
          (image.height * zoomScale) / (imageScale * zoom * this.scaleY)
        );
      }
    }

    getLabelZoomScale(zoom: number) {
      // When the frame is really zoomed out, the text should start zooming
      // as well. This reduces the likelihood of frame text overlapping.
      if (zoom < 0.5) {
        return zoom + 0.5;
      }
      return 1;
    }

    getTextWidth() {
      const zoom = this.canvas.getZoom();
      const zoomScale = this.getLabelZoomScale(zoom);
      return (
        ((this.textImage?.width ?? 0) * zoomScale) /
        (IMAGE_SCALE * zoom * this.scaleX)
      );
    }
  };
}

export default TitleBehaviors;
